import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { money } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Building2, Plus, RefreshCw, Store } from "lucide-react";

function defaultRange() {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 30);
  return {
    from: from.toISOString().slice(0, 10),
    to: to.toISOString().slice(0, 10),
  };
}

export default function Chain() {
  const qc = useQueryClient();
  const { switchOutlet, user } = useAuth();
  const [orgName, setOrgName] = useState("");
  const [outlet, setOutlet] = useState({ name: "", phone: "", city: "", address: "" });
  const [range, setRange] = useState(defaultRange);
  const [switching, setSwitching] = useState(null);

  const { data: orgData, isLoading } = useQuery({
    queryKey: ["org"],
    queryFn: async () => (await api.get("/org")).data,
  });

  const hasOrg = !!orgData?.org;
  const outlets = orgData?.outlets || [];

  const { data: reports, refetch: refetchReports, isFetching: reportsLoading } = useQuery({
    queryKey: ["org-reports", range.from, range.to],
    queryFn: async () => (await api.get(`/org/reports?from=${range.from}&to=${range.to}`)).data,
    enabled: hasOrg,
  });

  const createOrg = async () => {
    if (!orgName.trim()) return toast.error("Organization name required");
    try {
      await api.post("/org", { name: orgName.trim() });
      toast.success("Organization created");
      setOrgName("");
      qc.invalidateQueries({ queryKey: ["org"] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const addOutlet = async () => {
    if (!outlet.name.trim()) return toast.error("Outlet name required");
    try {
      await api.post("/org/outlets", outlet);
      toast.success("Outlet added");
      setOutlet({ name: "", phone: "", city: "", address: "" });
      qc.invalidateQueries({ queryKey: ["org"] });
      qc.invalidateQueries({ queryKey: ["org-reports"] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const doSwitch = async (restaurantId) => {
    if (restaurantId === orgData?.current_restaurant_id) return;
    setSwitching(restaurantId);
    try {
      await switchOutlet(restaurantId);
      toast.success("Switched outlet");
      window.location.reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setSwitching(null);
    }
  };

  if (isLoading) {
    return <div className="p-8 text-neutral-400">Loading chain…</div>;
  }

  return (
    <div className="h-full overflow-y-auto p-6 lg:p-8" data-testid="chain-page">
      <PageHeader
        title="Multi-outlet Chain"
        subtitle={hasOrg ? `${orgData.org.name} · consolidated ops` : "Create an organization to link outlets"}
      />

      {!hasOrg ? (
        <div className="kul-card p-6 max-w-lg space-y-3">
          <h3 className="font-display font-700 flex items-center gap-2">
            <Building2 className="w-4 h-4" /> Create organization
          </h3>
          <p className="text-sm text-neutral-500">
            Link this restaurant and future outlets under one org for switching and consolidated reports.
          </p>
          <div>
            <Label>Organization name</Label>
            <Input value={orgName} onChange={(e) => setOrgName(e.target.value)} placeholder="Ojent Hospitality Pvt Ltd" />
          </div>
          <Button onClick={createOrg} className="bg-[var(--kul-primary)] text-white">Create org</Button>
        </div>
      ) : (
        <div className="space-y-4 max-w-5xl">
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="kul-card p-5 space-y-3">
              <h3 className="font-display font-700 flex items-center gap-2">
                <Store className="w-4 h-4" /> Outlets ({outlets.length})
              </h3>
              <div className="space-y-2 max-h-[40vh] overflow-y-auto">
                {outlets.map((o) => {
                  const current = o.id === orgData.current_restaurant_id || o.id === user?.restaurant_id;
                  return (
                    <div key={o.id} className="flex items-center gap-3 border-b pb-2 last:border-0">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{o.name}</div>
                        <div className="text-xs text-neutral-400 truncate">
                          {[o.city, o.phone].filter(Boolean).join(" · ") || o.slug}
                        </div>
                      </div>
                      {current ? (
                        <Badge className="bg-green-100 text-green-700">Current</Badge>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={switching === o.id}
                          onClick={() => doSwitch(o.id)}
                        >
                          {switching === o.id ? "…" : "Switch"}
                        </Button>
                      )}
                    </div>
                  );
                })}
                {!outlets.length && <p className="text-sm text-neutral-400">No outlets yet.</p>}
              </div>
            </div>

            <div className="kul-card p-5 space-y-3">
              <h3 className="font-display font-700 flex items-center gap-2">
                <Plus className="w-4 h-4" /> Add outlet
              </h3>
              <div><Label>Name</Label><Input value={outlet.name} onChange={(e) => setOutlet({ ...outlet, name: e.target.value })} placeholder="Cafe Sunrise — Indiranagar" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>City</Label><Input value={outlet.city} onChange={(e) => setOutlet({ ...outlet, city: e.target.value })} /></div>
                <div><Label>Phone</Label><Input value={outlet.phone} onChange={(e) => setOutlet({ ...outlet, phone: e.target.value })} /></div>
              </div>
              <div><Label>Address</Label><Input value={outlet.address} onChange={(e) => setOutlet({ ...outlet, address: e.target.value })} /></div>
              <Button onClick={addOutlet} className="bg-[var(--kul-primary)] text-white">Add outlet</Button>
            </div>
          </div>

          <div className="kul-card p-5">
            <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
              <h3 className="font-display font-700">Consolidated reports</h3>
              <div className="flex flex-wrap items-end gap-2">
                <div>
                  <Label className="text-xs">From</Label>
                  <Input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} className="h-9" />
                </div>
                <div>
                  <Label className="text-xs">To</Label>
                  <Input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} className="h-9" />
                </div>
                <Button variant="outline" size="sm" onClick={() => refetchReports()} disabled={reportsLoading}>
                  <RefreshCw className={`w-3.5 h-3.5 mr-1 ${reportsLoading ? "animate-spin" : ""}`} /> Refresh
                </Button>
              </div>
            </div>
            {reports ? (
              <>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="rounded-xl bg-neutral-50 p-3">
                    <div className="text-xs text-neutral-500">Revenue</div>
                    <div className="font-display font-700 text-xl text-[var(--kul-primary)]">{money(reports.revenue)}</div>
                  </div>
                  <div className="rounded-xl bg-neutral-50 p-3">
                    <div className="text-xs text-neutral-500">Paid orders</div>
                    <div className="font-display font-700 text-xl">{reports.paid_orders}</div>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-neutral-400 border-b">
                        <th className="py-2 font-medium">Outlet</th>
                        <th className="py-2 font-medium">Revenue</th>
                        <th className="py-2 font-medium">Paid</th>
                        <th className="py-2 font-medium">Open</th>
                        <th className="py-2 font-medium">Low stock</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(reports.outlets || []).map((o) => (
                        <tr key={o.id} className="border-b last:border-0">
                          <td className="py-2 font-medium">{o.name}</td>
                          <td className="py-2 font-mono-k">{money(o.revenue)}</td>
                          <td className="py-2">{o.paid_orders}</td>
                          <td className="py-2">{o.open_orders}</td>
                          <td className="py-2">{o.low_stock}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <p className="text-sm text-neutral-400">Loading reports…</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
